export const API_ENDPOINTS = {
    auth: {
        signIn: 'auth/signin',
        signUp: 'auth/signup',
        forgotPassword: 'auth/forgot-password',
        resetPassword: 'auth/reset-password',
        switchEntity: 'auth/switch-entity',
        capabilities: 'auth/capabilities',
    },
    users: {
        list: 'users',
        adminList: 'users/admin',
        create: 'users',
        byId: (userId) => `users/${userId}`,
        status: (userId) => `users/${userId}/status`,
        changePassword: 'users/change-password',
        permissionOverrides: (userId) => `users/${userId}/permission-overrides`,
    },
    configs: {
        list: 'configs/all',
        currencies: 'currencies',
        services: 'services',
    },
    admin: {
        legalEntities: 'legal-entities',
        legalEntityLogo: (id) => `legal-entities/public/${id}/logo`,
        legalEntityFavicon: (id) => `legal-entities/public/${id}/favicon`,
        permissions: 'permissions',
        roles: 'roles',
        roleById: (roleId) => `roles/${roleId}`,
        rolePermissions: (roleId) => `roles/${roleId}/permissions`,
        auditLogs: 'audit-logs',
        salesPipelines: 'sales-pipeline',
        configs: 'configs',
        orgChart: 'org-chart',
        orgUnitRoles: (unitId) => `org-chart/units/${unitId}/roles`,
        userGroups: 'user-groups',
        groupPermissionOverrides: (groupId) =>
            `user-groups/${groupId}/permission-overrides`,
    },
    crm: {
        clients: 'clients',
        exportClients: 'clients/export',
        clientImport: 'clients/import',
        clientImportTemplate: 'clients/import/template',
        clientInfo: 'clients/info',
        contacts: 'contacts',
        allContacts: 'contacts/all',
    },
    fileImports: {
        list: 'file-imports',
        errorFile: (id) => `file-imports/${id}/error-file`,
    },
    sales: {
        opportunities: 'sales/opportunities',
        pipeline: 'sales/pipeline',
        pipelineOptions: 'sales/pipeline-options',
        searchOpportunities: 'sales/opportunities/search',
        contracts: 'contracts',
        exportContracts: 'contracts/export',
        allContracts: 'contracts/all',
        paymentTerms: 'contracts/payment-terms',
        contractInfo: 'contracts/info',
        contractCheckExist: 'contracts/check-exist',
        contractAttachments: (contractId) =>
            `contracts/${contractId}/attachments`,
        contractAttachmentDownload: (attachmentId) =>
            `contracts/attachments/${attachmentId}/download`,
    },
    headhunting: {
        applicants: 'headhunting/applicants',
        applicantCandidateDetail: (candidateId) =>
            `headhunting/applicants/candidate/${candidateId}/detail`,
        currentApplicants: 'headhunting/applicants/current-applicant',
        candidates: 'headhunting/candidates',
        candidateScreeningRequests: (candidateId) =>
            `headhunting/candidates/${candidateId}/screening-requests`,
        candidateScreenCv: (candidateId) =>
            `headhunting/candidates/${candidateId}/screen-cv`,
        candidateNote: (candidateId) =>
            `headhunting/candidates/${candidateId}/note`,
        candidateTentativeAvailableDate: (candidateId) =>
            `headhunting/candidates/${candidateId}/tentative-available-date`,
        candidateBulk: 'headhunting/candidates/bulk',
        candidateImportPreview: 'headhunting/candidates/import/preview',
        candidateImportCommit: 'headhunting/candidates/import/commit',
        requests: 'headhunting/headhunt-requests',
        createRequest: 'headhunting/headhunt-requests/create',
        allRequests: 'headhunting/headhunt-requests/all',
        requestClientContracts:
            'headhunting/headhunt-requests/client-contracts',
        exportRequests: 'headhunting/headhunt-requests/export',
        requestOwner: (requestId) =>
            `headhunting/headhunt-requests/${requestId}/owner`,
        requestStatus: (requestId) =>
            `headhunting/headhunt-requests/${requestId}/status`,
        requestPriority: (requestId) =>
            `headhunting/headhunt-requests/${requestId}/priority`,
        cancelRequest: (requestId) =>
            `headhunting/headhunt-requests/${requestId}/cancel`,
        requestJd: (requestId) =>
            `headhunting/headhunt-requests/${requestId}/jd`,
        requestPositionDetails: (requestId) =>
            `headhunting/headhunt-requests/${requestId}/position-details`,
        offers: 'headhunting/offers',
    },
    dashboard: {
        overview: 'dashboard/overview',
        headhuntingCandidatePipelines:
            'dashboard/headhunting/candidate-pipelines',
        salesPipelines: 'dashboard/sales/sales-pipelines',
        // Top-level dashboard endpoints (task 26.4)
        salesPipeline: 'dashboard/sales-pipeline',
        salesPipelineSummary: 'dashboard/sales-pipeline/summary',
        candidatePipeline: 'dashboard/candidate-pipeline',
        finance: 'dashboard/finance',
        detail: (type) => `dashboard/${type}/detail`,
    },
    kpi: {
        assignments: 'kpi/assignments',
        assignmentOptions: 'kpi/assignments/options',
        assignmentDrafts: 'kpi/assignments/drafts',
        assignmentById: (assignmentId) => `kpi/assignments/${assignmentId}`,
        assignmentCancel: (assignmentId) =>
            `kpi/assignments/${assignmentId}/cancel`,
        assignmentDuplicate: (assignmentId) =>
            `kpi/assignments/${assignmentId}/duplicate`,
        assignmentAssign: (assignmentId) =>
            `kpi/assignments/${assignmentId}/assign`,
        assignmentActualRecords: (assignmentId) =>
            `kpi/assignments/${assignmentId}/actual-records`,
        myDashboard: 'kpi/assignments/my-dashboard',
        teamDashboard: 'kpi/assignments/team-dashboard',
        comparison: 'kpi/comparison',
    },
    notifications: {
        list: 'notifications',
        unreadCount: 'notifications/unread-count',
        markRead: (notificationId) => `notifications/${notificationId}/read`,
    },
    finance: {
        invoices: 'invoices',
        receivableSummary: 'invoices/receivable-summary',
        paymentTracking: 'payment-tracking',
    },
    files: {
        cvUpload: 'file/cv/upload',
        cvView: (fileId) => `file/cv/${fileId}/view`,
        templates: 'file/templates',
        templateUploads: 'file/templates/uploads',
        templateById: (templateId) => `file/templates/${templateId}`,
        templateThumbnail: (templateId) =>
            `file/templates/${templateId}/thumbnail`,
        templateVersions: (templateCode) =>
            `file/templates/versions/${templateCode}`,
        templateSync: 'file/templates/sync',
        generatedFiles: 'file/generated-files',
        generatedFileCompose: 'file/generated-files/compose',
        generatedFilePreview: 'file/generated-files/preview',
        generatedFileById: (generatedFileId) =>
            `file/generated-files/${generatedFileId}`,
        generatedFileDownload: (generatedFileId, format) =>
            `file/generated-files/${generatedFileId}/download?format=${format}`,
    },
}
